/**
 * Eigenbau: Instanzweite und projektweite Registry-Einstellungen.
 *
 * `max_titel_laenge` und `standard_workflow_strikt` standen in der Registry;
 * nur die Titellänge war in der alten „Allgemein“-Seite erreichbar. Der
 * strikte Workflow gilt je Projekt und steuert erlaubte Statuswechsel.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { findeEinstellung } from "@cubic/shared";
import { Button } from "@/components/Button";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Input } from "@/components/Input";
import { Leerzustand } from "@/components/Leerzustand";
import { Schalter } from "@/components/entwurf/Schalter";
import { api } from "@/lib/api";
import { useIch, useProjekte } from "@/lib/haken";
import type { Meldung } from "@/lib/meldung";
import {
  EinstellungenHuelle,
  EinstellungsAbschnitt,
  EinstellungsZeile,
  Erfolgsmeldung,
  FehlerBlock,
  LadenZeile,
  meldungFuer,
} from "./gemeinsam";

const titelDefinition = findeEinstellung("max_titel_laenge")!;
const workflowDefinition = findeEinstellung("standard_workflow_strikt")!;
const titelMinimum =
  titelDefinition.typ === "ganze_zahl" ? titelDefinition.minimum : undefined;
const titelMaximum =
  titelDefinition.typ === "ganze_zahl" ? titelDefinition.maximum : undefined;

function hatInstanzRecht(rechte: readonly string[] | undefined): boolean {
  return (
    (rechte ?? []).includes("einstellungen.verwalten") ||
    (rechte ?? []).includes("system.admin")
  );
}

function WorkflowZeile({
  projektId,
  kuerzel,
  name,
  darfSchreiben,
  onOk,
  onFehler,
}: {
  projektId: string;
  kuerzel: string;
  name: string;
  darfSchreiben: boolean;
  onOk: (text: string) => void;
  onFehler: (m: Meldung) => void;
}) {
  const client = useQueryClient();
  const wert = useQuery({
    queryKey: [
      "cubic",
      "einstellung",
      "standard_workflow_strikt",
      "projekt",
      projektId,
    ],
    queryFn: () =>
      api.einstellungen.lies.query({
        schluessel: "standard_workflow_strikt",
        bereich: "projekt",
        bereichId: projektId,
      }),
  });

  const setzen = useMutation({
    mutationFn: (neu: string) =>
      api.einstellungen.setzen.mutate({
        schluessel: "standard_workflow_strikt",
        wert: neu,
        bereich: "projekt",
        bereichId: projektId,
      }),
    onSuccess: async (daten) => {
      onOk(`Workflow für ${kuerzel} gespeichert.`);
      await client.invalidateQueries({
        queryKey: [
          "cubic",
          "einstellung",
          "standard_workflow_strikt",
          "projekt",
          projektId,
        ],
      });
      // Cache der Antwort selbst aktualisieren
      client.setQueryData(
        [
          "cubic",
          "einstellung",
          "standard_workflow_strikt",
          "projekt",
          projektId,
        ],
        daten.wert,
      );
    },
    onError: (e) =>
      onFehler(meldungFuer(e, "Workflow-Einstellung konnte nicht gespeichert werden.")),
  });

  const an =
    (wert.data ?? workflowDefinition.standard) === "true";

  if (wert.isLoading) {
    return (
      <li className="flex items-center justify-between gap-3 border-b border-linie py-3">
        <span className="text-sm text-tinte3">
          <span className="font-mono text-[11px]">{kuerzel}</span> {name}
        </span>
        <LadenZeile text="…" />
      </li>
    );
  }

  if (wert.isError) {
    return (
      <li className="border-b border-linie py-3">
        <p className="text-sm text-tinte">
          <span className="font-mono text-[11px] text-tinte3">{kuerzel}</span>{" "}
          {name}
        </p>
        <FehlerBlock
          fehler={wert.error}
          fallback="Einstellung konnte nicht geladen werden."
          onErneut={() => void wert.refetch()}
        />
      </li>
    );
  }

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 border-b border-linie py-3">
      <div className="min-w-0">
        <p className="text-[14px] text-tinte">
          <span className="mr-2 font-mono text-[11px] text-tinte3">
            {kuerzel}
          </span>
          {name}
        </p>
        <p className="text-[12px] text-tinte3">
          {an
            ? "Nur benachbarte Schritte und Verwerfen"
            : "Jeder Statuswechsel erlaubt"}
        </p>
      </div>
      {darfSchreiben ? (
        <Schalter
          an={an}
          ariaLabel={`Strikter Workflow für ${name}`}
          onKlick={() => setzen.mutate(an ? "false" : "true")}
        />
      ) : (
        <span className="font-mono text-[11.5px] text-tinte3">
          {an ? "An" : "Aus"}
        </span>
      )}
    </li>
  );
}

export function EinstellungenInstanz() {
  const client = useQueryClient();
  const ich = useIch();
  const projekte = useProjekte();
  const kannInstanz = hatInstanzRecht(ich.data?.rechte);

  const maxTitel = useQuery({
    queryKey: ["cubic", "einstellung", "max_titel_laenge", "instanz"],
    queryFn: () =>
      api.einstellungen.lies.query({
        schluessel: "max_titel_laenge",
        bereich: "instanz",
      }),
  });

  const [titelLaenge, setzeTitelLaenge] = useState<string | null>(null);
  const [ok, setzeOk] = useState<string | null>(null);
  const [fehler, setzeFehler] = useState<Meldung | null>(null);

  useEffect(() => {
    if (maxTitel.data !== undefined && titelLaenge === null) {
      setzeTitelLaenge(maxTitel.data);
    }
  }, [maxTitel.data, titelLaenge]);

  const setzen = useMutation({
    mutationFn: (wert: string) =>
      api.einstellungen.setzen.mutate({
        schluessel: "max_titel_laenge",
        wert,
        bereich: "instanz",
      }),
    onSuccess: async (daten) => {
      setzeFehler(null);
      setzeOk("Titellänge gespeichert.");
      setzeTitelLaenge(daten.wert);
      await client.invalidateQueries({
        queryKey: ["cubic", "einstellung", "max_titel_laenge"],
      });
    },
    onError: (err) => {
      setzeOk(null);
      setzeFehler(meldungFuer(err, "Titellänge konnte nicht gespeichert werden."));
    },
  });

  const titelWert = titelLaenge ?? maxTitel.data ?? titelDefinition.standard;
  const titelZahl = Number(titelWert);
  const titelGueltig =
    /^\d+$/.test(titelWert) &&
    Number.isSafeInteger(titelZahl) &&
    titelZahl >= (titelMinimum ?? 1) &&
    titelZahl <= (titelMaximum ?? Number.MAX_SAFE_INTEGER);

  // Schreiben von Projekteinstellungen: Mitglied und höher (darfSchreiben).
  const darfProjektSchreiben = (projektId: string): boolean => {
    const rolle = ich.data?.projekte.find((p) => p.projektId === projektId)?.rolle;
    return (
      rolle === "mitglied" ||
      rolle === "betreuer" ||
      rolle === "verwalter"
    );
  };

  return (
    <EinstellungenHuelle
      aktiv="instanz"
      beschreibung="Grenzen der Instanz und Abläufe je Projekt."
    >
      {fehler ? <Fehlerzeile meldung={fehler} /> : null}
      {maxTitel.isError ? (
        <FehlerBlock
          fehler={maxTitel.error}
          fallback="Instanzeinstellungen konnten nicht geladen werden."
          onErneut={() => void maxTitel.refetch()}
        />
      ) : null}
      <Erfolgsmeldung text={ok} />

      <EinstellungsAbschnitt
        titel="Grenzen"
        beschreibung={titelDefinition.beschreibung}
      >
        <EinstellungsZeile
          label={titelDefinition.label}
          hinweis={
            kannInstanz
              ? `Ganze Zahl zwischen ${titelMinimum ?? 1} und ${titelMaximum ?? "—"}.`
              : "Nur lesbar — zum Ändern brauchst du „Einstellungen verwalten“."
          }
        >
          <Input
            type="number"
            className="w-28"
            value={titelWert}
            min={titelMinimum}
            max={titelMaximum}
            step={1}
            disabled={!kannInstanz || maxTitel.isLoading}
            onChange={(e) => {
              setzeTitelLaenge(e.target.value);
              setzeOk(null);
              setzeFehler(null);
            }}
            aria-label={titelDefinition.label}
          />
          {kannInstanz ? (
            <Button
              onClick={() => setzen.mutate(titelWert)}
              disabled={setzen.isPending || !titelGueltig}
              isLoading={setzen.isPending}
            >
              Speichern
            </Button>
          ) : null}
        </EinstellungsZeile>
      </EinstellungsAbschnitt>

      <EinstellungsAbschnitt
        titel="Workflow je Projekt"
        beschreibung={workflowDefinition.beschreibung}
      >
        {projekte.isLoading || ich.isLoading ? (
          <LadenZeile />
        ) : projekte.isError ? (
          <FehlerBlock
            fehler={projekte.error}
            fallback="Projekte konnten nicht geladen werden."
            onErneut={() => void projekte.refetch()}
          />
        ) : (projekte.data?.length ?? 0) === 0 ? (
          <Leerzustand
            titel="Kein Projekt sichtbar."
            hinweis="Sobald du einem Projekt beitrittst, erscheint hier der Workflow-Schalter."
          />
        ) : (
          <ul>
            {projekte.data!.map((p) => (
              <WorkflowZeile
                key={p.id}
                projektId={p.id}
                kuerzel={p.kuerzel}
                name={p.name}
                darfSchreiben={darfProjektSchreiben(p.id)}
                onOk={(text) => {
                  setzeFehler(null);
                  setzeOk(text);
                }}
                onFehler={(m) => {
                  setzeOk(null);
                  setzeFehler(m);
                }}
              />
            ))}
          </ul>
        )}
      </EinstellungsAbschnitt>
    </EinstellungenHuelle>
  );
}
